import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getAllVendorsLimited } from '../../services/paymentGatewayService';

type VendorFetchStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface VendorLimited {
  _id: string;
  label: string;
  name: string;
  vendorId: string;
}

interface PaymentGatewayState {
  vendorsLimited: VendorLimited[];
  isLoadingVendors: boolean;
  vendorsError: string | null;
  vendorsFetchStatus: VendorFetchStatus;
}

const initialState: PaymentGatewayState = {
  vendorsLimited: [],
  isLoadingVendors: false,
  vendorsError: null,
  vendorsFetchStatus: 'idle',
};

export const fetchVendorsLimited = createAsyncThunk(
  'paymentGateway/fetchVendorsLimited',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllVendorsLimited();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message || 'Failed to fetch vendors');
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch vendors');
    }
  }
);

const paymentGatewaySlice = createSlice({
  name: 'paymentGateway',
  initialState,
  reducers: {
    clearVendorsError: (state) => {
      state.vendorsError = null;
    },
    clearVendors: (state) => {
      state.vendorsLimited = [];
      state.vendorsFetchStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVendorsLimited.pending, (state) => {
        state.isLoadingVendors = true;
        state.vendorsError = null;
        state.vendorsFetchStatus = 'loading';
      })
      .addCase(fetchVendorsLimited.fulfilled, (state, action: PayloadAction<VendorLimited[]>) => {
        state.isLoadingVendors = false;
        state.vendorsLimited = action.payload;
        state.vendorsError = null;
        state.vendorsFetchStatus = 'succeeded';
      })
      .addCase(fetchVendorsLimited.rejected, (state, action) => {
        state.isLoadingVendors = false;
        state.vendorsError = action.payload as string;
        state.vendorsFetchStatus = 'failed';
      });
  },
});

export const { clearVendorsError, clearVendors } = paymentGatewaySlice.actions;

export default paymentGatewaySlice.reducer;
